import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import type { ExportData } from '@timetable/types'

// ── helpers ──────────────────────────────────────────────────────────────────

/** Parse a hex color and return [r, g, b] 0-255 */
function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '')
  const n = parseInt(clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean, 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

/** Return black or white depending on luminance of background */
function contrastColor(hex: string): [number, number, number] {
  const [r, g, b] = hexToRgb(hex)
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return lum > 0.5 ? [0, 0, 0] : [255, 255, 255]
}

function addPageHeader(doc: jsPDF, schoolName: string, academicYear: string) {
  const w = doc.internal.pageSize.getWidth()
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.text(schoolName, w / 2, 12, { align: 'center' })
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.text(`Academic Year: ${academicYear}`, w / 2, 18, { align: 'center' })
  doc.setDrawColor(180, 180, 180)
  doc.line(14, 21, w - 14, 21)
}

function addPageNumbers(doc: jsPDF) {
  const total = (doc as any).internal.getNumberOfPages()
  for (let i = 1; i <= total; i++) {
    doc.setPage(i)
    const w = doc.internal.pageSize.getWidth()
    const h = doc.internal.pageSize.getHeight()
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(120, 120, 120)
    doc.text(`Page ${i} of ${total}`, w / 2, h - 6, { align: 'center' })
    doc.setTextColor(0, 0, 0)
  }
}

// ── Master Data PDF ───────────────────────────────────────────────────────────

export function generateMasterDataPdf(exportData: ExportData, schoolName: string): Blob {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const { academicYear, teachers, subjects, classes, schedules } = exportData
  const periods = schedules.flatMap(s => s.periods)

  const sections: Array<{ title: string; head: string[][]; body: string[][] }> = [
    {
      title: 'Teachers',
      head: [['Name', 'Email', 'Phone', 'Teacher Type']],
      body: teachers.map((t) => [t.name, t.email ?? '', t.phone ?? '', t.teacher_type ?? '']),
    },
    {
      title: 'Subjects',
      head: [['Subject Name', 'Subject Code']],
      body: subjects.map((s) => [s.subject_name, s.subject_code ?? '']),
    },
    {
      title: 'Classes',
      head: [['Class Name', 'Division']],
      body: classes.map((c) => [c.class_name, c.class_group ?? '']),
    },
    {
      title: 'Periods',
      head: [['Period #', 'Start Time', 'End Time', 'Break']],
      body: periods.map((p) => [
        String(p.period_number),
        p.start_time,
        p.end_time,
        p.is_break ? 'Yes' : 'No',
      ]),
    },
  ]

  let first = true
  for (const section of sections) {
    if (!first) doc.addPage()
    first = false
    addPageHeader(doc, schoolName, academicYear)

    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.text(section.title, 14, 28)

    autoTable(doc, {
      startY: 32,
      head: section.head,
      body: section.body,
      styles: { fontSize: 9, cellPadding: 2, lineColor: [180, 180, 180], lineWidth: 0.3 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold', lineColor: [255, 255, 255], lineWidth: 0.3 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      tableLineColor: [180, 180, 180],
      tableLineWidth: 0.3,
      margin: { left: 14, right: 14 },
    })
  }

  addPageNumbers(doc)
  return doc.output('blob')
}

// ── Timetable PDF ─────────────────────────────────────────────────────────────

const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

/** Normalise day strings to Title Case so periods ("MONDAY") and timetable rows ("Monday") match */
function normalizeDay(day: string): string {
  return day.charAt(0).toUpperCase() + day.slice(1).toLowerCase()
}

export function generateTimetablePdf(
  exportData: ExportData,
  selectedClasses: string[], // "ClassName/Section" strings; empty = all
  schoolName: string
): Blob {
  const { academicYear, schedules, timetableRows, subjects } = exportData
  const periods = schedules.flatMap(s => s.periods)

  // Build subject color map
  const subjectColorMap = new Map<string, string>()
  for (const s of subjects) {
    if (s.color_code) subjectColorMap.set(s.subject_name, s.color_code)
  }

  // Normalise days on timetable rows (periods no longer have a day column)
  const normPeriods = periods.map((p) => ({ ...p }))
  const normRows = timetableRows.map((r) => ({ ...r, day: normalizeDay(r.day) }))

  // Determine unique class-sections present in timetable
  const allClassSections = [
    ...new Set(normRows.map((r) => `${r.class}/${r.section}`)),
  ].sort()
  const targets = selectedClasses.length === 0 ? allClassSections : selectedClasses

  const periodNums = [...new Set(normPeriods.map((p) => p.period_number))].sort((a, b) => {
    const aTime = normPeriods.find((p) => p.period_number === a)?.start_time ?? ''
    const bTime = normPeriods.find((p) => p.period_number === b)?.start_time ?? ''
    return aTime.localeCompare(bTime)
  })

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
  let first = true

  for (const cs of targets) {
    const [className, section] = cs.split('/')
    const rows = normRows.filter((r) => r.class === className && r.section === section)
    if (rows.length === 0) continue

    // Derive days only from this class-section's rows — prevents empty Saturday/Sunday
    // columns appearing for classes whose schedule doesn't include those days
    const days = [...new Set(rows.map((r) => r.day))].sort(
      (a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b)
    )

    if (!first) doc.addPage()
    first = false

    addPageHeader(doc, schoolName, academicYear)

    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.text(`Class: ${className}  |  Section: ${section}`, 14, 28)

    // Build grid: rows = periods, cols = days
    const head = [['Period', ...days]]
    const body: string[][] = periodNums.map((pn) => {
      const periodInfo = normPeriods.find((p) => p.period_number === pn)
      const label = periodInfo
        ? `${pn}\n${periodInfo.start_time.slice(0, 5)}–${periodInfo.end_time.slice(0, 5)}`
        : String(pn)
      return [
        label,
        ...days.map((day) => {
          const cell = rows.find((r) => r.day === day && r.period === pn)
          if (!cell) {
            // Check if this period is a break
            const p = normPeriods.find((p) => p.period_number === pn)
            return p?.is_break ? 'Break' : ''
          }
          return `${cell.subject}\n${cell.teacher}`
        }),
      ]
    })

    autoTable(doc, {
      startY: 32,
      head,
      body,
      styles: {
        fontSize: 8,
        cellPadding: 3,
        overflow: 'linebreak',
        minCellHeight: 12,
        lineColor: [180, 180, 180],
        lineWidth: 0.3,
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center',
        lineColor: [255, 255, 255],
        lineWidth: 0.3,
      },
      bodyStyles: { halign: 'center' },
      columnStyles: { 0: { fontStyle: 'bold', halign: 'center', cellWidth: 22, fillColor: [240, 240, 240] } },
      tableLineColor: [180, 180, 180],
      tableLineWidth: 0.3,
      margin: { left: 10, right: 10 },
      didParseCell(data) {
        if (data.section === 'body' && data.column.index > 0) {
          const cellText = Array.isArray(data.cell.raw)
            ? (data.cell.raw as string[]).join('\n')
            : String(data.cell.raw ?? '')
          const subjectName = cellText.split('\n')[0]
          const color = subjectColorMap.get(subjectName)
          if (color) {
            const [r, g, b] = hexToRgb(color)
            data.cell.styles.fillColor = [r, g, b]
            const [tr, tg, tb] = contrastColor(color)
            data.cell.styles.textColor = [tr, tg, tb]
          }
        }
      },
    })
  }

  if (first) {
    // No pages were added — add a blank page with a message
    addPageHeader(doc, schoolName, academicYear)
    doc.setFontSize(10)
    doc.text('No timetable records found for the selected classes.', 14, 35)
  }

  addPageNumbers(doc)
  return doc.output('blob')
}

// ── Teacher Timetable PDF ─────────────────────────────────────────────────────

export function generateTeacherTimetablePdf(
  exportData: ExportData,
  selectedTeachers: string[], // teacher names; empty = all
  schoolName: string
): Blob {
  const { academicYear, schedules, timetableRows, subjects } = exportData
  const periods = schedules.flatMap(s => s.periods)

  const subjectColorMap = new Map<string, string>()
  for (const s of subjects) {
    if (s.color_code) subjectColorMap.set(s.subject_name, s.color_code)
  }

  const normPeriods = periods.map((p) => ({ ...p }))
  const normRows = timetableRows.map((r) => ({ ...r, day: normalizeDay(r.day) }))

  const allTeachers = [...new Set(normRows.map((r) => r.teacher))].sort()
  const targets = selectedTeachers.length === 0 ? allTeachers : selectedTeachers

  const periodNums = [...new Set(normPeriods.map((p) => p.period_number))].sort((a, b) => {
    const aTime = normPeriods.find((p) => p.period_number === a)?.start_time ?? ''
    const bTime = normPeriods.find((p) => p.period_number === b)?.start_time ?? ''
    return aTime.localeCompare(bTime)
  })

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
  let first = true

  for (const teacher of targets) {
    const rows = normRows.filter((r) => r.teacher === teacher)
    if (rows.length === 0) continue

    if (!first) doc.addPage()
    first = false

    // Derive days from this teacher's rows only — no empty Saturday/Sunday columns
    const days = [...new Set(rows.map((r) => r.day))].sort(
      (a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b)
    )

    addPageHeader(doc, schoolName, academicYear)
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.text(`Teacher: ${teacher}`, 14, 28)

    const head = [['Period', ...days]]
    const body: string[][] = periodNums.map((pn) => {
      const periodInfo = normPeriods.find((p) => p.period_number === pn)
      const isBreak = periodInfo?.is_break ?? false
      const label = periodInfo
        ? `${pn}\n${periodInfo.start_time.slice(0, 5)}–${periodInfo.end_time.slice(0, 5)}`
        : String(pn)
      return [
        label,
        ...days.map((day) => {
          if (isBreak) return 'Break'
          const cell = rows.find((r) => r.day === day && r.period === pn)
          return cell ? `${cell.subject}\n${cell.class}/${cell.section}` : ''
        }),
      ]
    })

    autoTable(doc, {
      startY: 32,
      head,
      body,
      styles: {
        fontSize: 8,
        cellPadding: 3,
        overflow: 'linebreak',
        minCellHeight: 12,
        lineColor: [180, 180, 180],
        lineWidth: 0.3,
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center',
        lineColor: [255, 255, 255],
        lineWidth: 0.3,
      },
      bodyStyles: { halign: 'center' },
      columnStyles: { 0: { fontStyle: 'bold', halign: 'center', cellWidth: 22, fillColor: [240, 240, 240] } },
      tableLineColor: [180, 180, 180],
      tableLineWidth: 0.3,
      margin: { left: 10, right: 10 },
      didParseCell(data) {
        if (data.section === 'body' && data.column.index > 0) {
          const cellText = Array.isArray(data.cell.raw)
            ? (data.cell.raw as string[]).join('\n')
            : String(data.cell.raw ?? '')
          const subjectName = cellText.split('\n')[0]
          const color = subjectColorMap.get(subjectName)
          if (color) {
            const [r, g, b] = hexToRgb(color)
            data.cell.styles.fillColor = [r, g, b]
            const [tr, tg, tb] = contrastColor(color)
            data.cell.styles.textColor = [tr, tg, tb]
          }
        }
      },
    })
  }

  if (first) {
    addPageHeader(doc, schoolName, academicYear)
    doc.setFontSize(10)
    doc.text('No timetable records found for the selected teachers.', 14, 35)
  }

  addPageNumbers(doc)
  return doc.output('blob')
}
